import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classes from './CalendarPicker.module.scss';
import { Calendar, CalendarProps, CalendarViewChangeEvent } from 'primereact/calendar';
import { InputViewProps } from '../../forms/Input/Input.view';
import { FieldErrors, RegisterOptions, UseFormReturn, useWatch } from 'react-hook-form';
import classNames from 'classnames';
import inputClasses from '../../forms/form.module.scss';
import { FormElementAction, SetValueFunction } from '../../../models/basic-objects';
import { idGenerator } from '../../../helpers/idGenerator';
import { Loader } from '../../containers/Loader';
import { addLocale, locale } from 'primereact/api';
import { isSameMonth, toDate } from 'date-fns';
import { ErrorRender } from '../../containers/ErrorRender';
import { OutsideClickWrapper } from '../OutsideClickWrapper';
import { StartOfWeekFormatSetting } from 'models/graphql';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type CalendarPickerViewProps = {
  //
  name: string;
  updateAction?: FormElementAction<Date>;
  hideYear?: boolean;
  label?: string;
  useFormInstance: UseFormReturn;
  inputSize: InputViewProps['size'];
  rules: RegisterOptions;
  date?: Date;
  disabled?: boolean;
  placeholder?: string;
  calendarProps?: Omit<
    CalendarProps,
    | 'onChange'
    | 'placeholder'
    | 'inputClassName'
    | 'className'
    | 'value'
    | 'disabled'
    | 'numberOfMonths'
    | 'baseZIndex'
    | 'appendTo'
  >;
  startDay: StartOfWeekFormatSetting | undefined;
  minDate?: Date;
  maxDate?: Date;
  isWarning?: string;
  isError?: string;
};

const CalendarError = ({ errors, name }: { errors: FieldErrors; name: string }): JSX.Element | null => {
  if (errors) {
    return <ErrorRender errors={errors} name={name} className={classes.ErrorClass} />;
  }
  return null;
};
export const CalendarPickerView = (props: CalendarPickerViewProps): JSX.Element => {
  const calendarRef = useRef<Calendar>(null);
  const [date, setDate] = useState<CalendarProps['value']>(props.date);
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [executing, setExecuting] = useState<boolean>(false);
  const [, rerender] = useState({});
  const {
    calendarProps,
    disabled,
    hideYear,
    inputSize,
    name,
    placeholder,
    rules,
    updateAction,
    useFormInstance,
    isWarning,
    isError
  } = props;
  const {
    control,
    formState: { errors },
    setValue: setFormValue,
    getValues: getFormValues,
    trigger: triggerValidation,
    register: registerForm,
    clearErrors,
    getFieldState
  } = useFormInstance;

  const dateWatch: CalendarProps['value'] = useWatch({
    control,
    name
  });
  const startDay = props.startDay ?? StartOfWeekFormatSetting.MONDAY;
  const primitiveDateProp = props.date ? `${props.date.getTime()}` : undefined;

  let primitiveDate = '';
  if (Array.isArray(dateWatch)) {
    primitiveDate = dateWatch.reduce((prev, current) => `${prev}-${current.getTime()}`, '');
  } else if (dateWatch && dateWatch instanceof Date && !isNaN(dateWatch.getTime())) {
    primitiveDate = `${dateWatch.getTime()}`;
  }
  addLocale('en_MONDAY', {
    firstDayOfWeek: 1
  });
  addLocale('en_SUNDAY', {
    firstDayOfWeek: 0
  });
  locale(`en_${startDay}`);
  // Creating a custom locale that the week starts at monday

  const globalClasses = classNames(inputClasses.FormInputs, inputClasses[inputSize], classes.CalendarIcon, {
    [classes.CalendarIconDisabled]: disabled,
    [classes.CalendarIconUpdating]: executing,
    [inputClasses.FormInputsErrorClass]: (!!errors && getFieldState(name).error && !isWarning) || isError,
    [inputClasses.FormInputsWarningClass]: !getFieldState(name).error && isWarning && !isError
  });

  const labelClasses = classNames({
    [inputClasses.Label]: true
    // [inputClasses.LabelDisabled]: clonedProps.disabled
  });

  useEffect(() => {
    if (dateWatch) {
      useFormInstance.trigger(name).then(result => {
        if (!result) {
          rerender({});
        }
      });
    }
  }, [dateWatch, useFormInstance, name]);

  useEffect(() => {
    // in case we have set the date via useForm, we need to set the date value on the calendar itself
    if (!primitiveDate) return;
    if (primitiveDate.includes('-')) {
      // is array, split it
      const primitiveDates = primitiveDate.split('-').map(value => new Date(+value));
      setDate(primitiveDates);
    } else {
      const newDate = toDate(+primitiveDate);
      setDate(newDate);
    }
  }, [primitiveDate]);

  // in case we change default date, we need to update
  useEffect(() => {
    const formDate: Date | undefined = getFormValues(name);
    const primitiveFormDate = formDate && formDate?.getTime ? `${formDate.getTime()}` : undefined;
    const newDate = primitiveDateProp ? new Date(+primitiveDateProp) : undefined;
    setDate(newDate);
    if (primitiveFormDate !== primitiveDateProp) {
      setFormValue(name, newDate);
    }
  }, [primitiveDateProp, setDate, getFormValues, setFormValue, name]);

  const label = props.label ? <label className={labelClasses}>{props.label}</label> : null;

  useEffect(() => {
    if (disabled) {
      clearErrors(name);
      rerender({});
    }
  }, [disabled, name, clearErrors]);

  const onChange: CalendarProps['onChange'] = e => {
    if (e.value) {
      updateValue(e.value).catch(() => {
        // Prevents runtime error
      });
    }
  };

  const setValue: SetValueFunction<CalendarProps['value']> = useCallback(
    async (value, oldValue) => {
      if (value && oldValue !== value) {
        setFormValue(name, value, { shouldValidate: false, shouldTouch: true });
        try {
          const result = await triggerValidation(name);
          if (result && updateAction) {
            setExecuting(true);
            if (Array.isArray(value) || typeof value === 'string') return; // TODO we should pass a Date value not Date[] or string
            updateAction(value || '', (keepNewVal = true) => {
              if (!keepNewVal) {
                setFormValue(name, oldValue, {
                  shouldValidate: true,
                  shouldTouch: true
                });
              }
              setExecuting(false);
            });
          }
        } catch (e) {
          // prevents runtime
        }
      }
    },
    [updateAction, name, setExecuting, setFormValue, triggerValidation]
  );

  const updateValue = useCallback(
    async (value: CalendarProps['value']) => {
      const originalValue: CalendarProps['value'] = getFormValues(name);
      if (originalValue !== value) {
        try {
          await setValue(value, originalValue);
        } catch (e) {
          // prevents runtime error
        }
      }
    },

    [getFormValues, name, setValue]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    updateValue(new Date(e.target.value)).catch(() => {
      //prevents runtime error
    });
  };

  const register = registerForm(name, rules);
  const id = useMemo(() => idGenerator(name), [name]);

  const inputProps = {
    id,
    type: 'hidden',
    onChange: handleInputChange,
    name,
    onBlur: register.onBlur,
    ref: register.ref,
    'aria-labelledby': `${id}-label`
  };

  useEffect(() => {
    if (primitiveDateProp && calendarRef.current) {
      setViewDate(toDate(+primitiveDateProp));
    }
  }, [primitiveDateProp]);

  const hideCalendar = () => {
    if (calendarRef.current) {
      calendarRef.current.hide();
    }
  };
  const onViewDateChange = (e: CalendarViewChangeEvent) => setViewDate(e.value);

  const hideToday = !isSameMonth(viewDate, new Date(+primitiveDate));
  return (
    <OutsideClickWrapper onClickOutside={hideCalendar} onFocusOutside={hideCalendar}>
      <input {...inputProps} />
      {label}
      <Calendar
        ref={calendarRef}
        onChange={onChange}
        {...(disabled || executing ? { disabled: true } : {})}
        placeholder={placeholder ?? 'Select'}
        inputClassName={globalClasses}
        onViewDateChange={onViewDateChange}
        viewDate={viewDate}
        numberOfMonths={1}
        baseZIndex={+scssVars['z-index-overmodal']}
        onHide={() => {
          if (calendarRef.current) {
            calendarRef.current.getInput().blur();
          }
          setViewDate(primitiveDate === '' ? new Date() : toDate(+primitiveDate))
        }}
        panelClassName={`${hideYear ? 'hide-year' : ''} ${hideToday ? 'hide-today' : ''}`}
        {...calendarProps}
        value={date}
      />

      {executing ? (
        <div className={classes.LoaderWrapper}>
          <Loader variation={'dark'} size={'small'} />
        </div>
      ) : null}
      {}
      <CalendarError errors={errors} name={name} />
    </OutsideClickWrapper>
  );
};
